import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { formatApiMessage } from '@/api/client';
import { type TeamMember, usersApi } from '@/api/users.api';
import { AppShell } from '@/components/layout/AppShell';
import { useAuthStore } from '@/stores/auth.store';
import type { UserRole } from '@/types/auth';

const inviteSchema = z.object({
  email: z.string().email('Invalid email'),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  role: z.enum(['admin', 'manager', 'member', 'viewer']),
});
type InviteValues = z.infer<typeof inviteSchema>;

export function TeamPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [showInvite, setShowInvite] = useState(false);
  const [tab, setTab] = useState<'members' | 'pending'>('members');

  const { data: members, isLoading } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: usersApi.list,
  });

  const { data: pending = [] } = useQuery({
    queryKey: ['users', 'pending'],
    queryFn: usersApi.listPending,
    enabled: me?.role === 'owner' || me?.role === 'admin',
  });

  const approveMutation = useMutation({
    mutationFn: ({ uuid, role }: { uuid: string; role: 'admin' | 'manager' | 'member' | 'viewer' }) =>
      usersApi.approve(uuid, role),
    onSuccess: () => {
      toast.success('İstifadəçi təsdiqləndi');
      qc.invalidateQueries({ queryKey: ['users', 'pending'] });
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (uuid: string) => usersApi.reject(uuid),
    onSuccess: () => {
      toast.success('Qeydiyyat rədd edildi');
      qc.invalidateQueries({ queryKey: ['users', 'pending'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings.team_title')}</h1>
        <p className="page-subtitle">{t('settings.team_subtitle')}</p>
      </div>

      {(me?.role === 'owner' || me?.role === 'admin') && (
        <nav className="tabs mb-4">
          <button
            className={`tab ${tab === 'members' ? 'tab-active' : ''}`}
            onClick={() => setTab('members')}
          >
            {t('settings_team.tab_active_members')}
            {members && <span className="badge badge-muted ml-1.5">{members.length}</span>}
          </button>
          <button
            className={`tab ${tab === 'pending' ? 'tab-active' : ''}`}
            onClick={() => setTab('pending')}
          >
            {t('settings_team.tab_pending')}
            {pending.length > 0 && <span className="badge badge-warning ml-1.5">{pending.length}</span>}
          </button>
        </nav>
      )}

      {tab === 'pending' && (
        <PendingTable
          users={pending}
          onApprove={(uuid, role) => approveMutation.mutate({ uuid, role })}
          onReject={(uuid) => {
            if (confirm(t('settings_team.reject_confirm'))) rejectMutation.mutate(uuid);
          }}
          loading={approveMutation.isPending || rejectMutation.isPending}
        />
      )}

      {tab === 'members' && (
      <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted">
          {members ? t('settings_team.member_count', { count: members.length }) : '—'}
        </div>
        <button onClick={() => setShowInvite(true)} className="btn btn-primary">
          {t('settings_team.invite_btn')}
        </button>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFBFC] border-b border-border">
              <Th>{t('settings_team.th_user')}</Th>
              <Th>{t('settings_team.th_role')}</Th>
              <Th>{t('settings_team.th_status')}</Th>
              <Th>{t('settings_team.th_last_active')}</Th>
              <Th className="text-right">{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted text-sm">{t('common.loading')}</td></tr>
            )}
            {!isLoading && members?.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted text-sm">{t('settings_team.no_members')}</td></tr>
            )}
            {members?.map((m) => (
              <MemberRow key={m.id} member={m} isMe={m.id === me?.id} />
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
      </>
      )}
    </AppShell>
  );
}

function PendingTable({
  users,
  onApprove,
  onReject,
  loading,
}: {
  users: TeamMember[];
  onApprove: (uuid: string, role: 'admin' | 'manager' | 'member' | 'viewer') => void;
  onReject: (uuid: string) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="card !p-0">
      {users.length === 0 ? (
        <div className="text-center py-12 text-muted text-[13px]">
          {t('settings_team.no_pending')}
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('settings_team.th_user')}</th>
              <th>{t('settings_team.th_signed_up')}</th>
              <th className="text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <PendingRow
                key={u.id}
                user={u}
                onApprove={(role) => onApprove(u.id, role)}
                onReject={() => onReject(u.id)}
                loading={loading}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PendingRow({
  user,
  onApprove,
  onReject,
  loading,
}: {
  user: TeamMember;
  onApprove: (role: 'admin' | 'manager' | 'member' | 'viewer') => void;
  onReject: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<'admin' | 'manager' | 'member' | 'viewer'>('member');
  return (
    <tr>
      <td>
        <div className="font-medium text-gray-900">
          {user.firstName} {user.lastName}
        </div>
        <div className="text-[12px] text-muted">{user.email}</div>
      </td>
      <td className="text-[12px] text-muted">
        {new Date(user.createdAt).toLocaleDateString('az-AZ')}
      </td>
      <td className="text-right">
        <div className="flex justify-end gap-2 items-center">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'manager' | 'member' | 'viewer')}
            className="text-[12px] border border-border rounded-md px-2 py-1 bg-white"
          >
            <option value="viewer">{t('settings_team.role_viewer')}</option>
            <option value="member">{t('settings_team.role_member')}</option>
            <option value="manager">{t('settings_team.role_manager')}</option>
            <option value="admin">{t('settings_team.role_admin')}</option>
          </select>
          <button
            onClick={() => onApprove(role)}
            disabled={loading}
            className="btn btn-primary btn-sm"
          >
            {t('admin_users.approve')}
          </button>
          <button
            onClick={onReject}
            disabled={loading}
            className="btn btn-ghost btn-sm text-danger"
          >
            {t('admin_users.reject')}
          </button>
        </div>
      </td>
    </tr>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted ${className ?? ''}`}>
      {children}
    </th>
  );
}

function MemberRow({ member, isMe }: { member: TeamMember; isMe: boolean }) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const canManage = me?.role === 'owner' || me?.role === 'admin';
  const targetIsOwner = member.role === 'owner';
  const canEditTarget = canManage && !isMe && !targetIsOwner;

  const removeMutation = useMutation({
    mutationFn: () => usersApi.remove(member.id),
    onSuccess: () => {
      toast.success(t('settings_team.removed'));
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const roleMutation = useMutation({
    mutationFn: (role: UserRole) => usersApi.changeRole(member.id, role),
    onSuccess: () => {
      toast.success(t('settings_team.role_updated'));
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const resendMutation = useMutation({
    mutationFn: () => usersApi.resendInvite(member.id),
    onSuccess: (data) => toast.success(data.sent ? t('settings_team.resent') : t('settings_team.already_accepted')),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <tr className="border-b border-border last:border-0 hover:bg-[#FAFBFC]">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
            {(member.firstName[0] ?? '') + (member.lastName[0] ?? '')}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {member.firstName} {member.lastName}
              {isMe && <span className="ml-2 badge bg-info-bg text-info border-blue-200">{t('settings_team.you_badge')}</span>}
            </div>
            <div className="text-xs text-muted">{member.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {canEditTarget ? (
          <select
            className="input !py-1 !px-2 text-sm w-32"
            value={member.role}
            onChange={(e) => roleMutation.mutate(e.target.value as UserRole)}
            disabled={roleMutation.isPending}
          >
            <option value="admin">admin</option>
            <option value="manager">manager</option>
            <option value="member">member</option>
            <option value="viewer">viewer</option>
          </select>
        ) : (
          <RoleBadge role={member.role} />
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={member.status} />
      </td>
      <td className="px-4 py-3 text-sm text-muted">
        {member.lastLoginAt
          ? new Date(member.lastLoginAt).toLocaleDateString('az-AZ')
          : member.status === 'invited'
          ? t('settings_team.pending_invite')
          : '—'}
      </td>
      <td className="px-4 py-3 text-right">
        {canEditTarget && (
          <div className="flex items-center gap-1 justify-end">
            {member.status === 'invited' && (
              <button
                onClick={() => resendMutation.mutate()}
                className="btn btn-ghost btn-sm"
                disabled={resendMutation.isPending}
              >
                {t('settings_team.resend_invite')}
              </button>
            )}
            <button
              onClick={() => {
                if (confirm(t('settings_team.remove_confirm', { name: member.email }))) removeMutation.mutate();
              }}
              className="btn btn-ghost btn-sm text-danger hover:bg-danger-bg"
              disabled={removeMutation.isPending}
            >
              {t('settings_team.remove')}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function RoleBadge({ role }: { role: UserRole }) {
  const { t } = useTranslation();
  const map: Record<UserRole, string> = {
    owner: 'bg-purple-bg text-purple border-purple-200',
    admin: 'bg-info-bg text-info border-blue-200',
    manager: 'bg-gray-100 text-gray-800 border-border',
    member: 'bg-gray-100 text-gray-800 border-border',
    viewer: 'bg-gray-100 text-gray-700 border-border',
  };
  return <span className={`badge ${map[role]}`}>{t(`settings_team.role_${role}`)}</span>;
}

function StatusBadge({ status }: { status: TeamMember['status'] }) {
  const { t } = useTranslation();
  if (status === 'active') return <span className="badge bg-success-bg text-success border-emerald-200">{t('settings_team.status_active')}</span>;
  if (status === 'invited') return <span className="badge bg-warning-bg text-warning border-amber-200">{t('settings_team.status_invited')}</span>;
  if (status === 'suspended') return <span className="badge bg-danger-bg text-danger border-red-200">{t('settings_team.status_suspended')}</span>;
  return <span className="badge bg-gray-100 text-gray-700 border-border">{status}</span>;
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { role: 'member' },
  });

  const mutation = useMutation({
    mutationFn: usersApi.invite,
    onSuccess: (m) => {
      toast.success(t('settings_team.invite_sent_to', { email: m.email }));
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
      onClose();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div
      className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg border border-border shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-gray-900">{t('settings_team.invite_modal_title')}</h2>
          <p className="text-xs text-muted mt-0.5">{t('settings_team.invite_modal_subtitle')}</p>
        </div>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="px-5 py-4 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{t('settings_team.invite_first_name')}</label>
              <input className="input" {...register('firstName')} />
              {errors.firstName && <p className="text-xs text-danger mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="label">{t('settings_team.invite_last_name')}</label>
              <input className="input" {...register('lastName')} />
              {errors.lastName && <p className="text-xs text-danger mt-1">{errors.lastName.message}</p>}
            </div>
          </div>
          <div>
            <label className="label">{t('settings_team.invite_email_label')}</label>
            <input className="input" type="email" {...register('email')} placeholder="user@company.az" />
            {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className="label">{t('settings_team.invite_role')}</label>
            <select className="input" {...register('role')}>
              <option value="admin">{t('settings_team.role_admin')} — {t('settings_team.role_admin_hint')}</option>
              <option value="manager">{t('settings_team.role_manager')} — {t('settings_team.role_manager_hint')}</option>
              <option value="member">{t('settings_team.role_member')} — {t('settings_team.role_member_hint')}</option>
              <option value="viewer">{t('settings_team.role_viewer')} — {t('settings_team.role_viewer_hint')}</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-border">
            <button type="button" onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? t('common.sending') : t('settings_team.send_invite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
